<script setup>
import { computed } from 'vue';
import { useToggle } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { dynamicTime } from 'shared/helpers/timeHelper';
import { usePolicy } from 'dashboard/composables/usePolicy';
import {
  isPdfDocument,
  formatDocumentLink,
} from 'shared/helpers/documentHelper';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  id: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    default: '',
  },
  assistant: {
    type: Object,
    default: () => ({}),
  },
  externalLink: {
    type: String,
    required: true,
  },
  createdAt: {
    type: Number,
    required: true,
  },
  status: {
    type: String,
    default: 'available',
  },
  errorMessage: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['action']);
const { checkPermissions } = usePolicy();

const { t } = useI18n();

const [showActionsDropdown, toggleDropdown] = useToggle();

const menuItems = computed(() => {
  const allOptions = [
    {
      label: t('AIDEXACX.DOCUMENTS.OPTIONS.VIEW_RELATED_RESPONSES'),
      value: 'viewRelatedQuestions',
      action: 'viewRelatedQuestions',
      icon: 'i-ph-tree-view-duotone',
    },
  ];

  if (checkPermissions(['administrator'])) {
    if (isFailed.value) {
      allOptions.push({
        label: t('AIDEXACX.DOCUMENTS.OPTIONS.RETRY_DOCUMENT'),
        value: 'retry',
        action: 'retry',
        icon: 'i-ph-arrow-clockwise',
      });
    }
    allOptions.push({
      label: t('AIDEXACX.DOCUMENTS.OPTIONS.DELETE_DOCUMENT'),
      value: 'delete',
      action: 'delete',
      icon: 'i-lucide-trash',
    });
  }

  return allOptions;
});

const createdAt = computed(() => dynamicTime(props.createdAt));

const displayLink = computed(() => formatDocumentLink(props.externalLink));
const linkIcon = computed(() =>
  isPdfDocument(props.externalLink) ? 'i-ph-file-pdf' : 'i-ph-link-simple'
);

const isProcessing = computed(() => props.status === 'in_progress');
const isFailed = computed(() => props.status === 'failed');

const handleAction = ({ action, value }) => {
  toggleDropdown(false);
  emit('action', { action, value, id: props.id });
};
</script>

<template>
  <CardLayout>
    <div class="flex gap-1 justify-between w-full">
      <div class="flex gap-2 items-center min-w-0">
        <span class="text-base text-n-slate-12 line-clamp-1">
          {{ name }}
        </span>
        <span
          v-if="isProcessing"
          class="flex shrink-0 gap-1 items-center px-1.5 py-0.5 text-xs font-medium rounded-full text-n-amber-11 bg-n-amber-3"
        >
          <i class="i-ph-spinner animate-spin" />
          {{ $t('AIDEXACX.DOCUMENTS.STATUS.IN_PROGRESS') }}
        </span>
        <span
          v-else-if="isFailed"
          :title="errorMessage ? $t('AIDEXACX.DOCUMENTS.STATUS.FAILED_REASON') + errorMessage : undefined"
          class="flex shrink-0 gap-1 items-center px-1.5 py-0.5 text-xs font-medium rounded-full text-n-red-11 bg-n-red-3 cursor-help"
        >
          <i class="i-ph-warning-circle" />
          {{ $t('AIDEXACX.DOCUMENTS.STATUS.FAILED') }}
        </span>
      </div>
      <div class="flex gap-2 items-center">
        <div
          v-on-clickaway="() => toggleDropdown(false)"
          class="flex relative items-center group"
        >
          <Button
            icon="i-lucide-ellipsis-vertical"
            color="slate"
            size="xs"
            class="rounded-md group-hover:bg-n-alpha-2"
            @click="toggleDropdown()"
          />
          <DropdownMenu
            v-if="showActionsDropdown"
            :menu-items="menuItems"
            class="top-full mt-1 ltr:right-0 rtl:left-0 xl:ltr:right-0 xl:rtl:left-0"
            @action="handleAction($event)"
          />
        </div>
      </div>
    </div>
    <div v-if="isFailed && errorMessage" class="flex items-start gap-1.5 w-full -mt-1">
      <i class="i-ph-warning shrink-0 text-n-red-10 mt-0.5 text-xs" />
      <p class="text-xs text-n-red-10 break-all leading-relaxed">
        {{ $t('AIDEXACX.DOCUMENTS.STATUS.FAILED_REASON') }}{{ errorMessage }}
      </p>
    </div>
    <div class="flex gap-4 justify-between items-center w-full">
      <span
        class="flex gap-1 items-center text-sm truncate shrink-0 text-n-slate-11"
      >
        <i class="i-aidexa-aidexacx" />
        {{ assistant?.name || '' }}
      </span>
      <span
        class="flex flex-1 gap-1 justify-start items-center text-sm truncate text-n-slate-11"
      >
        <i :class="linkIcon" class="shrink-0" />
        <span class="truncate">{{ displayLink }}</span>
      </span>
      <div class="text-sm shrink-0 text-n-slate-11 line-clamp-1">
        {{ createdAt }}
      </div>
    </div>
  </CardLayout>
</template>
